import React, { useMemo } from 'react';
import Dropdown, { DropdownOption } from '../../../components/common/Dropdown';
import { ValidationError } from '../../../components/common/ErrorBanner';
import MultiCheckboxSelect from '../../../components/common/MultiCheckboxSelect';
import CustomDatepicker from '../../../components/common/DatePicker/Datepicker';
import { ValidationErrorDisplay } from '../../../components/common/FormFields';

const defaultOption: DropdownOption = {
  id: 0,
  label: 'All',
  value: 'All',
};

const defaultRegionOption: DropdownOption = {
  id: '',
  label: 'Select',
  value: '',
};

const AdvancedSearchSection: React.FC<{
  show: boolean;
  regionOptions: DropdownOption[];
  entityOptions: DropdownOption[];
  areaOptions: DropdownOption[];
  centerOptions: DropdownOption[];
  locationOptions: DropdownOption[];
  costCenterOptions: DropdownOption[];
  ratingOptions: DropdownOption[];
  rating: DropdownOption[];
  results: DropdownOption[];
  valueCompass: DropdownOption[];
  testOfChangeRating: DropdownOption[];
  // handlers
  handleRegion: (opt: DropdownOption | null) => void;
  handleEntity: (opt: DropdownOption | null) => void;
  handleArea: (opt: DropdownOption | null) => void;
  handleCenter: (opt: DropdownOption | null) => void;
  handleLocation: (opt: DropdownOption | null) => void;
  handleCostCenterChange: (selectedIds: (string | number)[], isAllSelected: boolean) => void;
  handleCostCenterSortBy: (v: string) => void;
  handleRatingChange: (selectedIds: (string | number)[], isAllSelected: boolean) => void;
  handleValueCompassChange: (selectedIds: (string | number)[], isAllSelected: boolean) => void;
  handleMetrixChange: (selectedIds: (string | number)[], isAllSelected: boolean) => void;
  handleResultsChange: (selectedIds: (string | number)[], isAllSelected: boolean) => void;
  handleTestOfChangeRatingChange: (selectedIds: (string | number)[], isAllSelected: boolean) => void;
  handleProjectTypeChange: (value: string) => void;
  // selected values / flags
  selectedRegionCode: DropdownOption;
  selectedEntityCode: DropdownOption;
  selectedAreaCode: DropdownOption;
  selectedCenterCode: DropdownOption;
  selectedLocationCode: DropdownOption;
  selectedCostCenterCode: { isAllSelected: boolean; selectedIds: (string | number)[] };
  selectedRating: { isAllSelected: boolean; selectedIds: (string | number)[] };
  selectedValueCompass: { isAllSelected: boolean; selectedIds: (string | number)[] };
  selectedMetrix: { isAllSelected: boolean; selectedIds: (string | number)[] };
  selectedResults: { isAllSelected: boolean; selectedIds: (string | number)[] };
  selectedTestOfChangeRating: { isAllSelected: boolean; selectedIds: (string | number)[] };
  selectedProjectType: string;
  sortByCostCenter: string;
  isLoadingData: boolean;
  validationErrors: ValidationError[];
  rangeStartDate?: string;
  rangeEndDate?: string;
  rangeStartError?: string;
  rangeEndError?: string;
  onRangeStartChange?: (name: string, value: string) => void;
  onRangeEndChange?: (name: string, value: string) => void;
  onRangeStartBlur?: () => void;
  onRangeEndBlur?: () => void;
  minROI?: string;
  maxROI?: string;
  onMinROIChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onMaxROIChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({
  show,
  regionOptions,
  entityOptions,
  areaOptions,
  centerOptions,
  locationOptions,
  costCenterOptions,
  ratingOptions,
  rating,
  results,
  valueCompass,
  testOfChangeRating,
  handleRegion,
  handleEntity,
  handleArea,
  handleCenter,
  handleLocation,
  handleCostCenterChange,
  handleCostCenterSortBy,
  handleRatingChange,
  handleValueCompassChange,
  handleMetrixChange,
  handleResultsChange,
  handleTestOfChangeRatingChange,
  handleProjectTypeChange,
  selectedRegionCode,
  selectedEntityCode,
  selectedAreaCode,
  selectedCenterCode,
  selectedLocationCode,
  selectedCostCenterCode,
  selectedRating,
  selectedValueCompass,
  selectedMetrix,
  selectedResults,
  selectedTestOfChangeRating,
  selectedProjectType,
  sortByCostCenter,
  isLoadingData,
  validationErrors,
  rangeStartDate = '',
  rangeEndDate = '',
  rangeStartError = '',
  rangeEndError = '',
  onRangeStartChange,
  onRangeEndChange,
  onRangeStartBlur,
  onRangeEndBlur,
  minROI = '',
  maxROI = '',
  onMinROIChange,
  onMaxROIChange,
}) => {
  const handleProjectTypeRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleProjectTypeChange(e.target.value);
  };

  // Derive effective selectedIds for each multi-select.
  // When isAllSelected=true and real options are available, always derive from current
  // options. This ensures all checkboxes appear checked on load and whenever the
  // options list refreshes (e.g., after a region change), without relying on Redux
  // selectedIds being pre-populated.
  // Each memo is fully independent — changing one field cannot affect another.
  const effectiveCostCenterIds = useMemo(() => {
    const hasRealOptions = costCenterOptions.length > 0 && costCenterOptions[0]?.value !== 'All';
    if (selectedCostCenterCode.isAllSelected && hasRealOptions) return costCenterOptions.map((o) => o.id);
    return selectedCostCenterCode.selectedIds;
  }, [selectedCostCenterCode, costCenterOptions]);

  const effectiveRatingIds = useMemo(() => {
    const hasRealOptions = rating.length > 0 && rating[0]?.value !== 'All';
    if (selectedRating.isAllSelected && hasRealOptions) return rating.map((o) => o.id);
    return selectedRating.selectedIds;
  }, [selectedRating, rating]);

  const effectiveValueCompassIds = useMemo(() => {
    const hasRealOptions = valueCompass.length > 0 && valueCompass[0]?.value !== 'All';
    if (selectedValueCompass.isAllSelected && hasRealOptions) return valueCompass.map((o) => o.id);
    return selectedValueCompass.selectedIds;
  }, [selectedValueCompass, valueCompass]);

  const effectiveMetrixIds = useMemo(() => {
    const hasRealOptions = ratingOptions.length > 0 && ratingOptions[0]?.value !== 'All';
    if (selectedMetrix.isAllSelected && hasRealOptions) return ratingOptions.map((o) => o.id);
    return selectedMetrix.selectedIds;
  }, [selectedMetrix, ratingOptions]);

  const effectiveResultsIds = useMemo(() => {
    const hasRealOptions = results.length > 0 && results[0]?.value !== 'All';
    if (selectedResults.isAllSelected && hasRealOptions) return results.map((o) => o.id);
    return selectedResults.selectedIds;
  }, [selectedResults, results]);

  const effectiveTestOfChangeRatingIds = useMemo(() => {
    const hasRealOptions = testOfChangeRating.length > 0 && testOfChangeRating[0]?.value !== 'All';
    if (selectedTestOfChangeRating.isAllSelected && hasRealOptions) return testOfChangeRating.map((o) => o.id);
    return selectedTestOfChangeRating.selectedIds;
  }, [selectedTestOfChangeRating, testOfChangeRating]);

  // Calculate date range: 5 years ago to today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const fiveYearsAgo = new Date();
  fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
  fiveYearsAgo.setHours(0, 0, 0, 0);

  // Wrapper handlers to transform 'All' to 'all' for area, center, and location
  const handleAreaWrapper = (opt: DropdownOption | null) => {
    if (opt?.value === 'All') {
      handleArea({ ...opt, value: 'all', label: 'All' });
    } else {
      handleArea(opt);
    }
  };

  const handleCenterWrapper = (opt: DropdownOption | null) => {
    if (opt?.value === 'All') {
      handleCenter({ ...opt, value: 'all', label: 'All' });
    } else {
      handleCenter(opt);
    }
  };

  const handleLocationWrapper = (opt: DropdownOption | null) => {
    if (opt?.value === 'All') {
      handleLocation({ ...opt, value: 'all', label: 'All' });
    } else {
      handleLocation(opt);
    }
  };

  return (
    <div className={show ? 'ml-10!' : 'hidden'}>
      <div className="flex w-1/2! gap-x-8 mt-8! mb-8!">
        <div className="flex-1">
          <Dropdown
            label="Region"
            selectedValue={selectedRegionCode}
            options={regionOptions.length ? regionOptions : [defaultRegionOption]}
            onSelect={handleRegion}
            menuClassName="w-full! max-h-60! overflow-y-auto!"
            placeholder="Select"
          />
        </div>
        <div className="flex-1">
          <Dropdown
            label="Entity"
            selectedValue={selectedEntityCode}
            options={entityOptions.length ? entityOptions : [defaultOption]}
            onSelect={handleEntity}
            menuClassName="w-full! max-h-60! overflow-y-auto!"
            buttonClassName="w-full!"
          />
        </div>
      </div>

      <div className="flex w-1/2! gap-x-8 mb-8!">
        <div className="flex-1">
          <Dropdown
            label="Area"
            selectedValue={selectedAreaCode}
            options={areaOptions.length ? areaOptions : [defaultOption]}
            onSelect={handleAreaWrapper}
            menuClassName="w-full! max-h-60! overflow-y-auto!"
            buttonClassName="w-full!"
          />
        </div>
        <div className="flex-1">
          <Dropdown
            label="Center"
            selectedValue={selectedCenterCode}
            options={centerOptions.length ? centerOptions : [defaultOption]}
            onSelect={handleCenterWrapper}
            menuClassName="w-full! max-h-60! overflow-y-auto!"
            buttonClassName="w-full!"
          />
        </div>
      </div>

      <div className="flex w-1/2! gap-x-8 mb-8!">
        <div className="w-1/2 pr-4!">
          <Dropdown
            label="Location"
            selectedValue={selectedLocationCode}
            options={locationOptions.length ? locationOptions : [defaultOption]}
            onSelect={handleLocationWrapper}
            menuClassName="w-full! max-h-60! overflow-y-auto!" 
            buttonClassName="w-full!"
          />
        </div>
      </div>

      <div className="flex w-full! flex-col gap-x-8">
        <div className="w-[90%] pr-4!">
          <div className={isLoadingData ? 'hidden' : ''} data-testid="multi-select-section">
              <div className="flex w-full! gap-x-10 mt-8! mb-8!">
                <div className="flex-1">
                  <MultiCheckboxSelect
                    label="Cost Center"
                    options={costCenterOptions.length ? costCenterOptions : [defaultOption]}
                    selectedValue={effectiveCostCenterIds}
                    onChange={handleCostCenterChange}
                    isSelectAll={selectedCostCenterCode.isAllSelected}
                    isSortBy
                    hideSelectAll={!costCenterOptions.length || costCenterOptions[0]?.value === 'All'}
                    sortByOptions={[
                      { value: 'id', label: 'Department ID' },
                      { value: 'name', label: 'Name' },
                    ]}
                    sortByValue={sortByCostCenter}
                    onSortByChange={handleCostCenterSortBy}
                  />
                </div>

                <div className="flex-1">
                  <MultiCheckboxSelect
                    label="Focus Area"
                    options={rating.length ? rating : [defaultOption]}
                    selectedValue={effectiveRatingIds}
                    isSelectAll={selectedRating.isAllSelected}
                    onChange={handleRatingChange}
                    maxHeight="max-h-[433px]"
                  />
                </div>

                <div className="flex-1">
                  <MultiCheckboxSelect
                    label="Value Compass"
                    options={valueCompass.length ? valueCompass : [defaultOption]}
                    selectedValue={effectiveValueCompassIds}
                    onChange={handleValueCompassChange}
                    isSelectAll={selectedValueCompass.isAllSelected}
                    hideSelectAll={!valueCompass.length || valueCompass[0]?.value === 'All'}
                  />
                </div>
              </div>

              <div className="flex w-full! mt-0 mb-0">
                <div className="w-2/3">
                  <MultiCheckboxSelect
                    label="Metrics"
                    options={ratingOptions.length ? ratingOptions : [defaultOption]}
                    selectedValue={effectiveMetrixIds}
                    onChange={handleMetrixChange}
                    isSelectAll={selectedMetrix.isAllSelected}
                  />
                </div>
              </div>

              <div className="flex w-full! gap-x-8 mt-8! mb-8!">
                <div className="w-[640px]">
                  <MultiCheckboxSelect
                    label="Results"
                    options={results.length ? results : [defaultOption]}
                    selectedValue={effectiveResultsIds}
                    onChange={handleResultsChange}
                    isSelectAll={selectedResults.isAllSelected}
                    hideSelectAll={true}
                  />
                </div>

                <div className="w-1/5">
                  <MultiCheckboxSelect
                    label="Test of Change Rating"
                    options={testOfChangeRating.length ? testOfChangeRating : [defaultOption]}
                    selectedValue={effectiveTestOfChangeRatingIds}
                    onChange={handleTestOfChangeRatingChange}
                    isSelectAll={selectedTestOfChangeRating.isAllSelected}
                    hideSelectAll={true}
                  />
                </div>

                <div className="w-[280px]">
                  <fieldset className="fieldset mb-2!">
                    <legend className="radio-legend">Searching closed and opened projects</legend>
                    <div className="mb-2! gap-x-1! flex items-center">
                      <input
                        className="cursor-pointer"
                        id="radio-project-type-0"
                        name="projectType"
                        type="radio"
                        value="Active"
                        checked={selectedProjectType === 'Active'}
                        onChange={handleProjectTypeRadioChange}
                      />
                      <label
                        htmlFor="radio-project-type-0"
                        className="label-ds2 cursor-pointer"
                      >
                        Open
                      </label>
                    </div>
                    <div className="mb-2! gap-x-1! flex items-center">
                      <input
                        className="cursor-pointer"
                        id="radio-project-type-1"
                        name="projectType"
                        type="radio"
                        value="Complete"
                        checked={selectedProjectType === 'Complete'}
                        onChange={handleProjectTypeRadioChange}
                      />
                      <label
                        htmlFor="radio-project-type-1"
                        className="label-ds2 cursor-pointer"
                      >
                        Closed
                      </label>
                    </div>
                    <div className="mb-2! gap-x-1! flex items-center">
                      <input
                        className="cursor-pointer"
                        id="radio-project-type-2"
                        name="projectType"
                        type="radio"
                        value="Both"
                        checked={selectedProjectType === 'Both'}
                        onChange={handleProjectTypeRadioChange}
                      />
                      <label
                        htmlFor="radio-project-type-2"
                        className="label-ds2 cursor-pointer"
                      >
                        Open and Closed
                      </label>
                    </div>
                  </fieldset>
                </div>
              </div>

              <div className="flex w-full! items-start gap-x-4 mt-20! mb-8!">
                <div className="flex-1">
                  <CustomDatepicker
                    name="rangeStartDate"
                    label="Range Start Activity"
                    value={rangeStartDate}
                    onChange={onRangeStartChange || (() => {})}
                    onBlur={onRangeStartBlur}
                    helperText={"MM-DD-YYYY"}
                    disabled={false}
                    error={rangeStartError}
                    minDate={fiveYearsAgo}
                    maxDate={today}
                  />
                </div>

                <div className="flex-1">
                  <CustomDatepicker
                    name="rangeEndDate"
                    label="Range End Activity"
                    value={rangeEndDate}
                    onChange={onRangeEndChange || (() => {})}
                    onBlur={onRangeEndBlur}
                    helperText={"MM-DD-YYYY"}
                    disabled={false}
                    error={rangeEndError}
                    minDate={fiveYearsAgo}
                    maxDate={today}
                  />
                </div>

                <div className="flex-1">
                  <div className="ds-form" data-ds-theme="vessel" data-ds-variant="input">
                    <label htmlFor="min-roi-input">Min ROI</label>
                    <input
                      autoComplete="off"
                      className="mt-0! w-full!"
                      id="min-roi-input"
                      type="text"
                      value={minROI}
                      onChange={onMinROIChange || (() => {})}
                    />
                  </div>
                </div>

                <div className="w-8 text-center pt-6! self-start mt-8">To</div>

                <div className="flex-1">
                  <div className="ds-form" data-ds-theme="vessel" data-ds-variant="input">
                    <label htmlFor="max-roi-input">Max ROI</label>
                    <input
                      autoComplete="off"
                      className="mt-0! w-full!"
                      id="max-roi-input"
                      type="text"
                      value={maxROI}
                      onChange={onMaxROIChange || (() => {})}
                    />
                  </div>
                </div>
              </div>

              {validationErrors.some((e) => e.field === 'costCenter') && (
                <ValidationErrorDisplay
                  message={`The max number of items from this list is 40. Your current selection contains ${selectedCostCenterCode.selectedIds.length}.`}
                  id="cost-center-error-description-list-1"
                />
              )}
              {!validationErrors.some((e) => e.field === 'costCenter') && !selectedCostCenterCode.isAllSelected && (
                <div className="text-border-gray">
                  <ul id="cost-center-information-description-list-1">
                    <li className="flex gap-x-1">

                      <span className="screenreader-only">
                        The max number of items from this list is 40. Your
                        current selection contains {selectedCostCenterCode.selectedIds.length}.
                      </span>{' '}
                      <p className="p4 m-0!">

                      </p>
                    </li>
                  </ul>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSearchSection;
